"use client";

import * as React from "react";
import {
  UsersManagementAction,
  UsersManagementState,
  useUsersManagementState,
} from "./users-management-state.js";
import { ListMetadata } from "../api/endpoint.js";

export interface UsersManagementContextType {
  state: UsersManagementState;
  dispatch: React.Dispatch<UsersManagementAction>;
}

const UsersManagementContext = React.createContext<
  UsersManagementContextType | undefined
>(undefined);
UsersManagementContext.displayName = "UsersManagementContext";

const initialState: UsersManagementState = {
  pagination: null,
  role: null,
  searchQuery: null,
};

export const UsersManagementContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useUsersManagementState(initialState);

  const context = React.useMemo<UsersManagementContextType>(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch],
  );

  return (
    <UsersManagementContext.Provider value={context}>
      {children}
    </UsersManagementContext.Provider>
  );
};

const NOOP = () => void 0;
const EMPTY_PAGINATION: ListMetadata = { before: null, after: null };

/**
 * The context may be provided if it is instantiated in the tree above the user.
 */
export function useUsersManagementContext(
  initialContext?: UsersManagementContextType | null,
): UsersManagementContextType {
  const context = React.useContext(UsersManagementContext);

  if (context) {
    return context;
  }

  if (initialContext) {
    return initialContext;
  }

  return {
    dispatch: NOOP,
    state: {
      pagination: EMPTY_PAGINATION,
      role: null,
      searchQuery: null,
    },
  };
}
